import { StatusColor } from "../types";
import { CheckCircle2, XCircle, Loader2, Clock, Minus, AlertCircle, Power } from 'lucide-react';
import React from 'react';

const inProgressStates = [
    "CREATE_IN_PROGRESS",
    "UPDATE_IN_PROGRESS",
    "UPDATE_COMPLETE_CLEANUP_IN_PROGRESS",
    "UPDATE_ROLLBACK_COMPLETE_CLEANUP_IN_PROGRESS",
    "UPDATE_ROLLBACK_IN_PROGRESS",
    "DELETE_IN_PROGRESS",
    "ROLLBACK_IN_PROGRESS"
];

const inFailedStates = [
    "CREATE_FAILED",
    "ROLLBACK_IN_PROGRESS",
    "ROLLBACK_FAILED",
    "ROLLBACK_COMPLETE",
    "DELETE_FAILED",
    "UPDATE_ROLLBACK_IN_PROGRESS",
    "UPDATE_ROLLBACK_FAILED",
    "UPDATE_ROLLBACK_COMPLETE_CLEANUP_IN_PROGRESS",
    "UPDATE_ROLLBACK_COMPLETE",
    "UPDATE_FAILED"
];

export function isResourceDeployed(status: string = "") {
    return [
        "DEPLOYED",
        "CREATE_COMPLETE",
        "UPDATE_COMPLETE",
        "UPDATE_ROLLBACK_COMPLETE",
        "UPDATE_IN_PROGRESS",
        "UPDATE_COMPLETE_CLEANUP_IN_PROGRESS",
        "UPDATE_ROLLBACK_COMPLETE_CLEANUP_IN_PROGRESS",
        "UPDATE_ROLLBACK_IN_PROGRESS",
        "ROLLBACK_IN_PROGRESS",
        "UPDATE_FAILED"
    ].includes(status);
}

export function getResourceStatusColor(status?: string) {
    if (!status) {
        return StatusColor.Default;
    }
    const activeStates = [
        "CREATE_COMPLETE",
        "UPDATE_COMPLETE",
        "UPDATE_ROLLBACK_COMPLETE"
    ];

    const failedStates = [
        "UPDATE_FAILED",
        "CREATE_FAILED",
        "DELETE_FAILED",
        "ROLLBACK_FAILED",
        "UPDATE_ROLLBACK_FAILED"
    ];

    return activeStates.includes(status) ?
        StatusColor.Active :
        inProgressStates.includes(status) ?
            StatusColor.Processing :
            failedStates.includes(status) ?
                StatusColor.Failed :
                StatusColor.Default
}

export function isDeploymentStatusInProgress(status: string) {
    return inProgressStates.includes(status);
}

export function isDeploymentStatusFailed(status: string) {
    return inFailedStates.includes(status);
}

export function getStatusAccent(status?: string) {
    if (!status) return { 
        color: "#9ca3af", 
        icon: <Minus size={10} />
    };
    
    // Success states - vibrant green (more prominent)
    if (["DEPLOYED", "CREATE_COMPLETE", "UPDATE_COMPLETE"].includes(status)) {
        return { 
            color: "#059669", 
            icon: <CheckCircle2 size={10} />
        };
    }
    
    // In progress states - blue
    if (["CREATE_IN_PROGRESS", "UPDATE_IN_PROGRESS", "UPDATE_COMPLETE_CLEANUP_IN_PROGRESS", 
         "ROLLBACK_IN_PROGRESS", "UPDATE_ROLLBACK_IN_PROGRESS", 
         "UPDATE_ROLLBACK_COMPLETE_CLEANUP_IN_PROGRESS"].includes(status)) {
        return { 
            color: "#3b82f6", 
            icon: <Loader2 size={10} style={{ animation: 'spin 1s linear infinite' }} />,
            animate: true 
        };
    }
    
    // Failed states - red
    if (["FAILED", "UPDATE_FAILED", "CREATE_FAILED", "DELETE_FAILED", 
         "ROLLBACK_FAILED", "UPDATE_ROLLBACK_FAILED"].includes(status)) {
        return { 
            color: "#ef4444", 
            icon: <XCircle size={10} />
        };
    }
    
    // Delete in progress - orange
    if (["DELETE_IN_PROGRESS"].includes(status)) {
        return { 
            color: "#f59e0b", 
            icon: <Loader2 size={10} style={{ animation: 'spin 1s linear infinite' }} />,
            animate: true 
        };
    }
    
    // Rollback complete - warning orange
    if (["ROLLBACK_COMPLETE", "UPDATE_ROLLBACK_COMPLETE"].includes(status)) {
        return { 
            color: "#f59e0b", 
            icon: <AlertCircle size={10} />
        };
    }
    
    // Queued - gray with animation
    if (["DEPLOY_QUEUED", "QUEUED"].includes(status)) {
        return { 
            color: "#9ca3af", 
            icon: <Clock size={10} />,
            animate: true 
        };
    }
    
    // Deleted - lighter, more muted gray (clearly different from success)
    if (status === "DELETE_COMPLETE") {
        return { 
            color: "#9ca3af", 
            icon: <Power size={10} />
        };
    }
    
    // Not deployed - lighter gray
    if (status === "NOT_DEPLOYED") {
        return { 
            color: "#9ca3af", 
            icon: <Minus size={10} />
        };
    }
    
    // Canceled - lighter gray
    if (status === "DEPLOY_CANCELED") {
        return { 
            color: "#9ca3af", 
            icon: <Minus size={10} />
        };
    }
    
    // Default/Unknown - lighter gray
    return { 
        color: "#9ca3af", 
        icon: <Minus size={10} />
    };
}

export function mapDeploymentStatus(status?: string) {
    if (!status) {
        return {
            status: "Not deployed"
        };
    }
    else if ([
        "ROLLBACK_COMPLETE",
        "UPDATE_ROLLBACK_COMPLETE"
    ].includes(status)) {
        return {
            status: "Rollbacked"
        };
    }
    else if (["DEPLOY_QUEUED", "QUEUED"].includes(status)) {
        return {
            status: "Queued"
        }
    }
    else if (status === "DELETE_COMPLETE") {
        return {
            status: "UNDEPLOYED"
        };
    }
    else if (status === "NOT_DEPLOYED") {
        return {
            status: "Not deployed"
        };
    }
    else if ([
        "DEPLOYED",
        "CREATE_COMPLETE",
        "UPDATE_COMPLETE"
    ].includes(status)) {
        return {
            status: "Deployed"
        }
    }
    else if ([
        "CREATE_IN_PROGRESS"
    ].includes(status)) {
        return {
            status: "Deploying"
        }
    }
    else if ([
        "UPDATE_IN_PROGRESS",
        "UPDATE_COMPLETE_CLEANUP_IN_PROGRESS"
    ].includes(status)) {
        return {
            status: "Deploying"
        }
    } else if (status === "DELETE_IN_PROGRESS") {
        return {
            status: "Deleting"
        }
    } else if ([
        "ROLLBACK_IN_PROGRESS",
        "UPDATE_ROLLBACK_COMPLETE_CLEANUP_IN_PROGRESS",
        "UPDATE_ROLLBACK_IN_PROGRESS"
    ].includes(status)) {
        return {
            status: "Rollbacking"
        };
    } else if ([
        "FAILED",
        "UPDATE_FAILED",
        "CREATE_FAILED",
        "DELETE_FAILED",
        "ROLLBACK_FAILED",
        "UPDATE_ROLLBACK_FAILED",
    ].includes(status)) {
        return {
            status: "Failed"
        }
    } else if (status === "DEPLOY_CANCELED") {
        return {
            status: "Canceled"
        }
    } else {
        return {
            status: "Unknown status"
        }
    }
}

export default function DeploymentStatusTag(props: { status?: string, size?: "small" | "large" }) {
    const statusAccent = getStatusAccent(props.status);
    const { status } = mapDeploymentStatus(props.status);
    const fontSize = props.size === "large" ? "10px" : "9px";
    const padding = props.size === "large" ? "3px 8px" : "2px 6px";
    
    // Make success states more prominent with darker text color
    const isSuccess = ["DEPLOYED", "CREATE_COMPLETE", "UPDATE_COMPLETE"].includes(props.status || "");
    const textColor = isSuccess ? "#047857" : statusAccent.color; // Darker green for better contrast
    const iconSize = props.size === "large" ? "11px" : "10px";

    return (
        <div style={{
            fontSize: fontSize,
            fontWeight: 700, // Bolder font weight for success states
            textTransform: "uppercase",
            letterSpacing: "0.04em", // Reduced letter spacing for smaller size
            color: textColor,
            flexShrink: 0,
            padding: padding,
            borderRadius: "3px",
            backgroundColor: `${statusAccent.color}10`, // More subtle background
            border: `1px solid ${statusAccent.color}20`,
            display: "inline-flex",
            alignItems: "center",
            gap: "4px"
        }}>
            <span style={{ 
                color: statusAccent.color,
                display: "inline-flex",
                alignItems: "center"
            }}>
                {React.cloneElement(statusAccent.icon, { style: { ...statusAccent.icon.props.style, fontSize: iconSize } })}
            </span>
            {status}
        </div>
    );
};